/**
 * MappingTab Component
 * Tab za mapiranje XML polja na bazu
 */

import { useState } from 'react';
import {
  Grid,
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  Button,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Save as SaveIcon,
  Storage as DatabaseIcon,
} from '@mui/icons-material';
import { ParsedFeed, FieldTransformOptions } from '../../../lib/xmlParser';
import { CustomField, ArrayPreviewData } from '../types';
import {
  CORE_FIELDS,
  EXTRA_FIELDS,
} from '../utils/fieldConfig';
import { getArrayValues } from '../utils/helpers';
import { XmlTreeView } from './XmlTreeView';
import { FieldMappingRow } from './FieldMappingRow';
import { SampleNavigator } from './SampleNavigator';
import { CustomFieldDialog } from './CustomFieldDialog';
import { ArrayPreviewDialog } from './ArrayPreviewDialog';

interface MappingTabProps {
  parsedFeed: ParsedFeed;
  feedName: string;
  mappings: Record<string, string>;
  fieldOptions: Record<string, FieldTransformOptions>;
  customFields: CustomField[];
  sampleIndex: number;
  expanded: Set<string>;
  onMappingChange: (dbField: string, xmlPath: string) => void;
  onFieldOptionChange: (dbField: string, option: Partial<FieldTransformOptions>) => void;
  onAddCustomField: (field: CustomField) => void;
  onRemoveCustomField: (id: string) => void;
  onSampleIndexChange: (index: number) => void;
  onToggleExpand: (path: string) => void;
  onSave: () => void;
  saveDisabled: boolean;
}

export function MappingTab({
  parsedFeed,
  feedName,
  mappings,
  fieldOptions,
  customFields,
  sampleIndex,
  expanded,
  onMappingChange,
  onFieldOptionChange,
  onAddCustomField,
  onRemoveCustomField,
  onSampleIndexChange,
  onToggleExpand,
  onSave,
  saveDisabled,
}: MappingTabProps) {
  const [showAddField, setShowAddField] = useState(false);
  const [arrayPreviewOpen, setArrayPreviewOpen] = useState(false);
  const [arrayPreviewData, setArrayPreviewData] = useState<ArrayPreviewData>({
    field: '',
    values: [],
  });

  const handlePreviewArray = (fieldPath: string) => {
    const values = getArrayValues(
      parsedFeed.sampleRecords,
      sampleIndex,
      fieldPath,
      parsedFeed.recordElement
    );
    setArrayPreviewData({ field: fieldPath, values });
    setArrayPreviewOpen(true);
  };

  // Check which extra fields are active (have mapping defined)
  const activeExtraFields = EXTRA_FIELDS.filter(f => mappings[f.key] !== undefined);
  const inactiveExtraFields = EXTRA_FIELDS.filter(f => mappings[f.key] === undefined);

  return (
    <Grid container spacing={2}>
      {/* XML Structure Tree */}
      <Grid item xs={12} lg={4}>
        <XmlTreeView
          structure={parsedFeed.structure}
          sampleRecords={parsedFeed.sampleRecords}
          sampleIndex={sampleIndex}
          recordElement={parsedFeed.recordElement}
          expanded={expanded}
          onToggleExpand={onToggleExpand}
        />
      </Grid>

      {/* Mapping */}
      <Grid item xs={12} lg={8}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="subtitle2">Mapiranje na bazu</Typography>
            <Chip
              size="small"
              label={`${parsedFeed.fields.length} polja dostupno`}
              color="info"
              variant="outlined"
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              size="small"
              startIcon={<AddIcon />}
              onClick={() => setShowAddField(true)}
            >
              Dodaj custom polje
            </Button>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<SaveIcon />}
              onClick={onSave}
              disabled={saveDisabled}
            >
              Spremi profil
            </Button>
          </Box>
        </Box>

        {/* Add Custom Field Dialog */}
        {showAddField && (
          <CustomFieldDialog
            onAddField={onAddCustomField}
            onClose={() => setShowAddField(false)}
          />
        )}

        {/* Sample Record Navigator */}
        <SampleNavigator
          sampleIndex={sampleIndex}
          onSampleIndexChange={onSampleIndexChange}
          totalSamples={parsedFeed.sampleRecords.length}
          totalRecords={parsedFeed.totalRecords}
        />

        <Paper variant="outlined" sx={{ maxHeight: 450, overflow: 'auto' }}>
          {/* Auto Dobavljač Info */}
          <Box sx={{ p: 1.5, bgcolor: 'info.dark', display: 'flex', alignItems: 'center', gap: 1 }}>
            <DatabaseIcon fontSize="small" />
            <Typography variant="body2">
              <strong>Dobavljač (automatski):</strong> {feedName || '(unesi naziv profila)'}
            </Typography>
          </Box>

          {/* Core Fields Section */}
          <Box sx={{ p: 1, bgcolor: 'grey.900' }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              OSNOVNA POLJA
            </Typography>
          </Box>
          <Table size="small">
            <TableBody>
              {CORE_FIELDS.map(field => (
                <FieldMappingRow
                  key={field.key}
                  field={field}
                  mapping={mappings[field.key] || ''}
                  fieldOptions={fieldOptions[field.key]}
                  availableFields={parsedFeed.fields}
                  sampleRecords={parsedFeed.sampleRecords}
                  sampleIndex={sampleIndex}
                  recordElement={parsedFeed.recordElement}
                  onMappingChange={onMappingChange}
                  onFieldOptionChange={onFieldOptionChange}
                  onPreviewArray={handlePreviewArray}
                />
              ))}
            </TableBody>
          </Table>

          {/* Custom Fields Section */}
          {customFields.length > 0 && (
            <>
              <Box sx={{ p: 1, bgcolor: 'grey.900', borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  CUSTOM POLJA ({customFields.length})
                </Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  {customFields.map(field => (
                    <FieldMappingRow
                      key={field.id}
                      field={{
                        key: field.key,
                        label: field.label,
                        ...field,
                      } as any}
                      mapping={mappings[field.key] || ''}
                      fieldOptions={fieldOptions[field.key]}
                      availableFields={parsedFeed.fields}
                      sampleRecords={parsedFeed.sampleRecords}
                      sampleIndex={sampleIndex}
                      recordElement={parsedFeed.recordElement}
                      isCustom
                      onMappingChange={onMappingChange}
                      onFieldOptionChange={onFieldOptionChange}
                      onRemove={() => onRemoveCustomField(field.id)}
                      onPreviewArray={handlePreviewArray}
                    />
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {/* Active Extra Fields Section */}
          {activeExtraFields.length > 0 && (
            <>
              <Box sx={{ p: 1, bgcolor: 'grey.900', borderTop: 1, borderColor: 'divider' }}>
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  DODATNA POLJA
                </Typography>
              </Box>
              <Table size="small">
                <TableBody>
                  {activeExtraFields.map(field => (
                    <FieldMappingRow
                      key={field.key}
                      field={field}
                      mapping={mappings[field.key] || ''}
                      fieldOptions={fieldOptions[field.key]}
                      availableFields={parsedFeed.fields}
                      sampleRecords={parsedFeed.sampleRecords}
                      sampleIndex={sampleIndex}
                      recordElement={parsedFeed.recordElement}
                      onMappingChange={onMappingChange}
                      onFieldOptionChange={onFieldOptionChange}
                      onRemove={() => {
                        // Remove mapping and options
                        onMappingChange(field.key, '');
                        onFieldOptionChange(field.key, { mode: 'single' });
                      }}
                      onPreviewArray={handlePreviewArray}
                    />
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {/* Quick Add Extra Fields */}
          <Box sx={{ p: 1, bgcolor: 'grey.900', borderTop: 1, borderColor: 'divider' }}>
            <Typography variant="caption" fontWeight="bold" color="text.secondary">
              DODAJ POLJE (klikni)
            </Typography>
          </Box>
          <Box sx={{ p: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {inactiveExtraFields.map(field => (
              <Chip
                key={field.key}
                label={field.label}
                size="small"
                variant="outlined"
                onClick={() => onMappingChange(field.key, '')}
              />
            ))}
          </Box>
        </Paper>
      </Grid>

      {/* Array Preview Dialog */}
      <ArrayPreviewDialog
        open={arrayPreviewOpen}
        onClose={() => setArrayPreviewOpen(false)}
        data={arrayPreviewData}
      />
    </Grid>
  );
}
